/**
 * The brand's catalog looks on the Art Style page: pure helpers behind `useSiteLooks`, the strip and `SiteLookView`.
 *
 * Besides its own art styles (spark-creatives `art_styles`), a brand's website can show any catalog look compatible
 * with its design (owner-hub `site_art_styles`, a casino-creation banner set each). "Primary" is the look the website
 * shows now, one across the whole strip: a catalog look, or the brand's own look (`owner-<tenant>`) drawn from the
 * custom style spark-creatives last sent to the site (`spark_style_id`).
 */
import type { TFunction } from 'i18next';

import {
  isOwnerLookId,
  type ArtStyleSummary,
  type LookSample,
  type SiteLook,
  type SiteLooks,
  type SiteRegenState,
} from './art-style-api';
import { artStyleLabel } from './useActiveArtStyle';
import { defaultStyleId } from './useArtStyles';

/** What the page has open: one of the brand's own styles (`?style=`) or one of its catalog looks (`?look=`). */
export type ArtStyleSelection = { kind: 'style'; id: string } | { kind: 'look'; id: string };

/** Whether the catalog looks could be read: not yet, read, not deployed here (404), or a failed first read. */
export type SiteLooksStatus = 'loading' | 'ok' | 'unavailable' | 'failed';

/** A catalog name as owners read it: a slug ("neon-arcade") reads as words, a written name as it is. Pure. */
export const lookLabel = (name: string): string => (/\s/.test(name.trim()) ? name.trim() : artStyleLabel(name));

/** What the website shows now: the site job's answer (it follows the job), else the looks', else "" (not known). */
export const liveStyleIdOf = (state: SiteRegenState | null, looks: SiteLooks | null): string =>
  state?.live_style_id || looks?.live_style_id || '';

/**
 * The custom style the website shows: the one last sent to the site while the site shows the brand's own look; none
 * while a catalog look shows (or nothing is known). Pure.
 */
export const livePrimaryStyleId = (state: SiteRegenState | null, liveStyleId: string): string | undefined =>
  liveStyleId && isOwnerLookId(liveStyleId) && state?.spark_style_id ? state.spark_style_id : undefined;

/** One card of the strip, in its place. Keys are prefixed, so a style id and a look id can never collide. */
export type StripEntry =
  | { kind: 'style'; key: string; style: ArtStyleSummary; primary: boolean }
  | { kind: 'look'; key: string; look: SiteLook; primary: boolean; busy: boolean };

export const styleKey = (id: string): string => `style:${id}`;
export const lookKey = (id: string): string => `look:${id}`;
export const selectionKey = (selection: ArtStyleSelection | undefined): string | undefined =>
  selection ? (selection.kind === 'look' ? lookKey(selection.id) : styleKey(selection.id)) : undefined;

/**
 * The strip's cards in order: the Primary card first (whatever kind), then the brand's own styles (in their order), then
 * the other catalog looks (the original first, then the service's order). Without catalog looks the order is the styles'
 * own (the strip as it was). Pure.
 */
export const stripEntries = ({
  styles,
  looks,
  primaryStyleId,
  liveStyleId,
  busyLookId,
}: {
  styles: readonly ArtStyleSummary[];
  /** Null when the catalog looks are not available (then Primary is the active style, as before them). */
  looks: readonly SiteLook[] | null;
  primaryStyleId: string | undefined;
  liveStyleId: string;
  busyLookId: string;
}): StripEntry[] => {
  if (looks === null) {
    const cards: StripEntry[] = styles.map((style) => ({
      kind: 'style',
      key: styleKey(style.id),
      style,
      primary: style.status === 'active',
    }));
    // The primary leads the row here too (right after "New style", which the strip draws first).
    const first = cards.find((entry) => entry.primary);
    return first ? [first, ...cards.filter((entry) => entry !== first)] : cards;
  }
  const styleCards: StripEntry[] = styles.map((style) => ({
    kind: 'style',
    key: styleKey(style.id),
    style,
    primary: style.id === primaryStyleId,
  }));
  const ordered = [...looks].sort((a, b) => Number(b.original) - Number(a.original));
  const lookCards: StripEntry[] = ordered.map((look) => ({
    kind: 'look',
    key: lookKey(look.style_id),
    look,
    primary: liveStyleId ? look.style_id === liveStyleId : look.live,
    busy: look.style_id === busyLookId,
  }));
  const primary = [...styleCards, ...lookCards].find((entry) => entry.primary);
  return [
    ...(primary ? [primary] : []),
    ...styleCards.filter((entry) => entry !== primary),
    ...lookCards.filter((entry) => entry !== primary),
  ];
};

/**
 * What the page opens: the look or style the URL names when it is still the brand's, else (once everything it depends
 * on has been read) the default, which the URL then takes over. The default is the Primary card (the live catalog look,
 * or the custom style the site shows); with nothing live, the styles' own default (the active style, else the newest);
 * with no styles, the original look (else the first). `fallback` is set only when the URL needs replacing. Pure.
 */
export const resolveSelection = ({
  requestedStyle,
  requestedLook,
  styles,
  looks,
  looksStatus,
  siteSettled,
  liveStyleId,
  primaryStyleId,
}: {
  requestedStyle: string;
  requestedLook: string;
  /** Undefined until the list is read. */
  styles: readonly ArtStyleSummary[] | undefined;
  looks: SiteLooks | null;
  looksStatus: SiteLooksStatus;
  /** The site job's first read has settled (it names the custom style the site shows). */
  siteSettled: boolean;
  liveStyleId: string;
  primaryStyleId: string | undefined;
}): { selection: ArtStyleSelection | undefined; fallback: ArtStyleSelection | undefined } => {
  const catalog = looksStatus === 'ok' && looks ? looks.looks : [];
  if (requestedLook && catalog.some((look) => look.style_id === requestedLook)) {
    return { selection: { kind: 'look', id: requestedLook }, fallback: undefined };
  }
  if (requestedStyle && styles?.some((style) => style.id === requestedStyle)) {
    return { selection: { kind: 'style', id: requestedStyle }, fallback: undefined };
  }
  // A look named before the looks are read waits for them (it may well be one of them).
  if (looksStatus === 'loading' || styles === undefined) return { selection: undefined, fallback: undefined };
  if (catalog.length === 0) {
    const id = defaultStyleId(styles);
    const fallback: ArtStyleSelection | undefined = id ? { kind: 'style', id } : undefined;
    return { selection: fallback, fallback };
  }
  if (!siteSettled) return { selection: undefined, fallback: undefined };
  let fallback: ArtStyleSelection | undefined;
  const liveLook = catalog.find((look) => (liveStyleId ? look.style_id === liveStyleId : look.live));
  if (liveLook) fallback = { kind: 'look', id: liveLook.style_id };
  else if (primaryStyleId && styles.some((style) => style.id === primaryStyleId)) {
    fallback = { kind: 'style', id: primaryStyleId };
  } else if (styles.length > 0) {
    const id = defaultStyleId(styles);
    fallback = id ? { kind: 'style', id } : undefined;
  } else {
    const original = catalog.find((look) => look.original) ?? catalog[0];
    fallback = { kind: 'look', id: original.style_id };
  }
  return { selection: fallback, fallback };
};

/** The ratios Website Creatives draws (2:1 hero, 1:1 tile, 1:2 portrait, 3:1 strip), in the order a preview shows them. */
const PREVIEW_RATIOS: readonly number[] = [2, 1, 0.5, 3];

/** The most tiles a look's preview shows. */
export const MAX_LOOK_TILES = 6;

const near = (a: number, b: number): boolean => Math.abs(a - b) < 0.05;

/**
 * The tiles a look's preview shows, from the brand's own art in it: one of each ratio Website Creatives uses (each from
 * a different banner when it can), then more (new banners first, the hero ratio first), at most `max`. Pure.
 */
export const pickLookTiles = (samples: readonly LookSample[], max = MAX_LOOK_TILES): LookSample[] => {
  const usable = samples.filter((sample) => sample.aspect > 0);
  const picked: LookSample[] = [];
  const usedBanners = new Set<string>();
  const take = (sample: LookSample): void => {
    picked.push(sample);
    usedBanners.add(sample.deployment_uuid);
  };
  for (const ratio of PREVIEW_RATIOS) {
    if (picked.length >= max) break;
    const ofRatio = usable.filter((sample) => near(sample.aspect, ratio) && !picked.includes(sample));
    const fresh = ofRatio.find((sample) => !usedBanners.has(sample.deployment_uuid)) ?? ofRatio[0];
    if (fresh) take(fresh);
  }
  const rest = usable
    .filter((sample) => !picked.includes(sample))
    .sort(
      (a, b) =>
        Number(usedBanners.has(a.deployment_uuid)) - Number(usedBanners.has(b.deployment_uuid)) ||
        Number(near(b.aspect, 2)) - Number(near(a.aspect, 2)),
    );
  for (const sample of rest) {
    if (picked.length >= max) break;
    if (picked.some((p) => p.url === sample.url)) continue;
    take(sample);
  }
  return picked;
};

/** A ratio as owners read it (2 → "2:1", 0.5 → "1:2"). Pure. */
export const ratioLabel = (aspect: number): string => {
  if (aspect <= 0) return '';
  if (aspect >= 1) {
    const w = Math.round(aspect * 10) / 10;
    return `${w}:1`;
  }
  const h = Math.round((1 / aspect) * 10) / 10;
  return `1:${h}`;
};

/** A look's status line. Pure (given `t`). */
export const lookStatusCopy = (
  look: SiteLook,
  { primary, drawing, switching }: { primary: boolean; drawing: boolean; switching: boolean },
  t: TFunction,
): string => {
  if (switching) return t('Switching your site to this look…');
  if (drawing) return t('Lumio is drawing your website art in this look. Players keep seeing your current look.');
  if (primary) return t('Your website shows this look now.');
  if (look.art === 'ready')
    return t('Your website art in this look is ready. Set it as primary to show it to players.');
  if (look.art === 'partial') {
    return t('Some of your website art is drawn in this look. Generate the rest before you switch.');
  }
  return t('No website art in this look yet. Generate it to preview this look with your own content.');
};
